// src/components/coordinateGrid/InequalityRenderer.tsx
/**
 * InequalityRenderer Component
 * 
 * Renders inequality regions with shading.
 * Phase 7: Vectors and Inequalities
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../types/redux';
import {
  selectVisibleInequalities,
  selectFeasibleRegions,
  selectInequality,
  selectSelectedInequalities,
} from '../../store/slices/inequalitiesSlice';
import { Inequality, FeasibleRegion, Point2D } from '../../types/coordinateGrid';
import { sampleEquation, parseEquation } from '../../lib/coordinateGrid/equationParser';

interface InequalityRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

/**
 * Generate a polygon path for the shaded region
 * This creates a large polygon covering the appropriate half-plane
 */
function generateRegionPath(
  inequality: Inequality,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  width: number,
  height: number,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): string {
  const parsed = parseEquation(inequality.boundaryEquation.expression);
  if (!parsed.isValid) return '';

  // Sample points along the boundary line
  const boundaryPoints = sampleEquation(parsed, xMin, xMax, 200);
  if (boundaryPoints.length === 0) return '';

  // For vertical lines
  if (parsed.type === 'vertical') {
    const k = parsed.coefficients.k;
    if (k === undefined) return '';

    // Create a rectangle covering the appropriate half-plane
    const xScreen = mathToScreen({ x: k, y: 0 }, scaleX, scaleY, offsetX, offsetY).x;

    let x1, x2;
    if (inequality.type === '<' || inequality.type === '<=') {
      // Shade left of the line
      x1 = 0;
      x2 = xScreen;
    } else {
      // Shade right of the line
      x1 = xScreen;
      x2 = width;
    }

    return `M ${x1} 0 L ${x2} 0 L ${x2} ${height} L ${x1} ${height} Z`;
  }

  // For non-vertical lines, create a polygon covering the half-plane
  // We'll use the boundary line and extend to the edges of the viewport
  const screenPoints = boundaryPoints.map((p) =>
    mathToScreen(p, scaleX, scaleY, offsetX, offsetY)
  );

  // Determine which side to shade based on inequality type
  // Test a point above the line (in screen coordinates, "above" means smaller y)
  const testPoint = screenPoints[Math.floor(screenPoints.length / 2)];
  const testYAbove = testPoint.y - 10; // Point above the line
  const testYBelow = testPoint.y + 10; // Point below the line

  // Convert back to math coordinates to test
  const testMathYAbove = (offsetY - testYAbove) / scaleY;
  const testMathYBelow = (offsetY - testYBelow) / scaleY;
  const testMathX = (testPoint.x - offsetX) / scaleX;

  // Evaluate boundary at test x
  const boundaryY = parsed.type === 'vertical'
    ? null
    : (() => {
        const { type, coefficients } = parsed;
        if (type === 'horizontal') return coefficients.k;
        if (type === 'linear') {
          const m = coefficients.m ?? 0;
          const c = coefficients.c ?? 0;
          return m * testMathX + c;
        }
        if (type === 'quadratic') {
          const a = coefficients.a ?? 0;
          const b = coefficients.b ?? 0;
          const c = coefficients.c ?? 0;
          return a * testMathX * testMathX + b * testMathX + c;
        }
        return null;
      })();

  if (boundaryY === null) return '';

  // Determine which side to shade
  const shadeAbove = (() => {
    if (inequality.type === '>' || inequality.type === '>=') {
      return testMathYAbove > boundaryY;
    } else {
      return testMathYAbove < boundaryY;
    }
  })();

  // Create polygon path
  // Start from a corner of the viewport
  const corners: { x: number; y: number }[] = [
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height },
  ];

  // Find intersection points with viewport edges
  const intersections: { x: number; y: number }[] = [];
  
  // Top edge (y = 0 in screen, y = yMax in math)
  const topMathY = offsetY / scaleY;
  // Bottom edge (y = height in screen, y = yMin in math)
  const bottomMathY = -(height - offsetY) / scaleY;
  // Left edge (x = 0 in screen, x = xMin in math)
  const leftMathX = -offsetX / scaleX;
  // Right edge (x = width in screen, x = xMax in math)
  const rightMathX = (width - offsetX) / scaleX;

  // Add boundary points that are within viewport
  for (const point of screenPoints) {
    if (
      point.x >= 0 &&
      point.x <= width &&
      point.y >= 0 &&
      point.y <= height
    ) {
      intersections.push(point);
    }
  }

  // Add corner points on the correct side
  for (const corner of corners) {
    const cornerMathX = (corner.x - offsetX) / scaleX;
    const cornerMathY = (offsetY - corner.y) / scaleY;

    // Evaluate boundary at corner x
    const cornerBoundaryY = parsed.type === 'vertical'
      ? null
      : (() => {
          const { type, coefficients } = parsed;
          if (type === 'horizontal') return coefficients.k;
          if (type === 'linear') {
            const m = coefficients.m ?? 0;
            const c = coefficients.c ?? 0;
            return m * cornerMathX + c;
          }
          if (type === 'quadratic') {
            const a = coefficients.a ?? 0;
            const b = coefficients.b ?? 0;
            const c = coefficients.c ?? 0;
            return a * cornerMathX * cornerMathX + b * cornerMathX + c;
          }
          return null;
        })();

    if (cornerBoundaryY !== null) {
      const isOnCorrectSide = shadeAbove
        ? cornerMathY > cornerBoundaryY
        : cornerMathY < cornerBoundaryY;

      if (isOnCorrectSide) {
        intersections.push(corner);
      }
    }
  }

  // Sort points to create a proper polygon
  if (intersections.length < 3) return '';

  // Create path
  let path = `M ${intersections[0].x} ${intersections[0].y}`;
  for (let i = 1; i < intersections.length; i++) {
    path += ` L ${intersections[i].x} ${intersections[i].y}`;
  }
  path += ' Z';

  return path;
}

export const InequalityRenderer: React.FC<InequalityRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const inequalities = useSelector(selectVisibleInequalities);
  const feasibleRegions = useSelector(selectFeasibleRegions);
  const selectedInequalities = useSelector(selectSelectedInequalities);

  // Handle inequality selection
  const handleInequalityClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(selectInequality({ id, ctrlKey: e.ctrlKey || e.metaKey }));
    },
    [dispatch]
  );

  // Calculate visible domain
  const xMin = -offsetX / scaleX;
  const xMax = (width - offsetX) / scaleX;
  const yMin = -(height - offsetY) / scaleY;
  const yMax = offsetY / scaleY;

  // Render inequality regions
  const inequalityElements = useMemo(() => {
    return inequalities.map((inequality) => {
      const regionPath = generateRegionPath(
        inequality,
        xMin,
        xMax,
        yMin,
        yMax,
        width,
        height,
        scaleX,
        scaleY,
        offsetX,
        offsetY
      );

      const isSelected = inequality.isSelected;

      return (
        <g key={inequality.id} className={`inequality-${inequality.id}`}>
          {/* Shaded region */}
          <path
            d={regionPath}
            fill={inequality.fillColor}
            fillOpacity={inequality.fillOpacity}
            stroke="none"
            onClick={(e) => handleInequalityClick(inequality.id, e)}
            style={{ cursor: 'pointer' }}
          />

          {/* Boundary line */}
          {(() => {
            const parsed = parseEquation(inequality.boundaryEquation.expression);
            if (!parsed.isValid) return null;

            const boundaryPoints = sampleEquation(parsed, xMin, xMax, 200);
            if (boundaryPoints.length === 0) return null;

            const screenPoints = boundaryPoints.map((p) =>
              mathToScreen(p, scaleX, scaleY, offsetX, offsetY)
            );

            let pathData = '';
            if (parsed.type === 'vertical') {
              const x = screenPoints[0].x;
              pathData = `M ${x} 0 L ${x} ${height}`;
            } else if (parsed.type === 'horizontal') {
              const y = screenPoints[0].y;
              pathData = `M 0 ${y} L ${width} ${y}`;
            } else {
              pathData = `M ${screenPoints[0].x} ${screenPoints[0].y}`;
              for (let i = 1; i < screenPoints.length; i++) {
                pathData += ` L ${screenPoints[i].x} ${screenPoints[i].y}`;
              }
            }

            return (
              <path
                d={pathData}
                stroke={inequality.boundaryEquation.color}
                strokeWidth={inequality.boundaryEquation.thickness}
                strokeDasharray={inequality.boundaryStyle === 'dashed' ? '8,4' : 'none'}
                fill="none"
                onClick={(e) => handleInequalityClick(inequality.id, e)}
                style={{ cursor: 'pointer' }}
              />
            );
          })()}

          {/* Selection highlight */}
          {isSelected && (
            <path
              d={regionPath}
              fill="none"
              stroke={isDark ? '#60A5FA' : '#2563EB'}
              strokeWidth={3}
              strokeDasharray="4,4"
              opacity={0.8}
              pointerEvents="none"
            />
          )}
        </g>
      );
    });
  }, [
    inequalities,
    width,
    height,
    scaleX,
    scaleY,
    offsetX,
    offsetY,
    xMin,
    xMax,
    yMin,
    yMax,
    isDark,
    handleInequalityClick,
  ]);

  // Render feasible regions (intersections)
  const feasibleRegionElements = useMemo(() => {
    return feasibleRegions.map((region, index) => {
      const regionInequalities = inequalities.filter((i) =>
        region.inequalityIds.includes(i.id)
      );

      if (regionInequalities.length === 0) return null;

      // For feasible regions, we need to find the intersection
      // This is a simplified version - full implementation would require
      // polygon intersection algorithms
      return (
        <g key={`feasible-${index}`} className="feasible-region">
          {/* Highlight the intersection area */}
          {regionInequalities.map((inequality) => {
            const regionPath = generateRegionPath(
              inequality,
              xMin,
              xMax,
              yMin,
              yMax,
              width,
              height,
              scaleX,
              scaleY,
              offsetX,
              offsetY
            );

            return (
              <path
                key={inequality.id}
                d={regionPath}
                fill={region.highlightColor}
                fillOpacity={0.5}
                stroke={region.highlightColor}
                strokeWidth={2}
                strokeDasharray="4,4"
                opacity={0.7}
              />
            );
          })}
        </g>
      );
    });
  }, [
    feasibleRegions,
    inequalities,
    width,
    height,
    scaleX,
    scaleY,
    offsetX,
    offsetY,
    xMin,
    xMax,
    yMin,
    yMax,
  ]);

  return (
    <g className="inequality-layer">
      {/* Render feasible regions first (so they appear behind individual inequalities) */}
      {feasibleRegionElements}
      {/* Render individual inequalities */}
      {inequalityElements}
    </g>
  );
};
